import { patch, readonly, writable, type ReadableStore, type Unsubscribe } from '../store.js'
import { createSpeakingDetector, levelFromDb, rmsDb } from './speaking.js'

/**
 * Local speaking detection and a level meter, from Web Audio on ONE track.
 *
 *   AudioContext → MediaStreamAudioSourceNode → AnalyserNode
 *   RMS of the analyser's time-domain window every 50 ms → hysteresis
 *
 * Only the local microphone is analysed. Everyone else's ring comes from
 * application state, broadcast on transitions — never from decoding their
 * audio a second time.
 */

export interface AudioLevelState {
  speaking: boolean
  /** 0..1, fast attack and slow release, for a meter. */
  level: number
}

export class AudioLevelMeter {
  private readonly store = writable<AudioLevelState>({ speaking: false, level: 0 })
  readonly state: ReadableStore<AudioLevelState> = readonly(this.store)
  private readonly detector = createSpeakingDetector()
  private ctx: AudioContext | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private analyser: AnalyserNode | null = null
  private buffer: Float32Array<ArrayBuffer> | null = null
  private timer: ReturnType<typeof setInterval> | null = null
  private track: MediaStreamTrack | null = null
  private readonly unfollow: Unsubscribe

  /** Follows `track`: a device switch swaps the track object, and the analyser follows it. */
  constructor(track: ReadableStore<MediaStreamTrack | null>) {
    this.unfollow = track.subscribe((t) => this.attach(t))
  }

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx
    if (typeof AudioContext === 'undefined') return null
    const ctx = new AudioContext()
    this.ctx = ctx
    // Created after the join click, so normally running. Where a browser
    // still starts it suspended, the next tap anywhere resumes it.
    if (ctx.state === 'suspended') {
      void ctx.resume()
      document.addEventListener('pointerdown', () => void this.ctx?.resume(), { once: true })
    }
    return ctx
  }

  private publish(db: number): void {
    const s = this.store.get()
    const speaking = this.detector.update(db, performance.now())
    const target = levelFromDb(db)
    const level = target > s.level ? target : s.level * 0.8 + target * 0.2
    patch(this.store, { speaking, level })
  }

  private tick(): void {
    const t = this.track
    if (!this.analyser || !this.buffer || !t) return
    // The media controller hands us a clone that stays enabled through a
    // mute, so speech is still measured while muted — that is how "you're
    // muted" gets said. What a muted mic's speech means is its decision.
    if (t.readyState !== 'live') {
      this.detector.reset()
      this.publish(-Infinity)
      return
    }
    this.analyser.getFloatTimeDomainData(this.buffer)
    this.publish(rmsDb(this.buffer))
  }

  private detach(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    this.source?.disconnect()
    this.source = null
    this.analyser = null
    this.buffer = null
    this.detector.reset()
    patch(this.store, { speaking: false, level: 0 })
  }

  private attach(t: MediaStreamTrack | null): void {
    this.detach()
    this.track = t
    if (!t) return
    const c = this.ensureContext()
    if (!c) return
    const analyser = c.createAnalyser()
    // 2048 samples is ~43 ms at 48 kHz — the plan's "~50 ms window". A shorter
    // one lands on zero crossings often enough to make the level twitchy.
    analyser.fftSize = 2048
    analyser.smoothingTimeConstant = 0
    // Analysis only. Nothing connects to the destination, so there is no
    // local echo of your own voice.
    this.source = c.createMediaStreamSource(new MediaStream([t]))
    this.source.connect(analyser)
    this.analyser = analyser
    this.buffer = new Float32Array(analyser.fftSize)
    this.timer = setInterval(() => this.tick(), 50)
  }

  close(): void {
    this.unfollow()
    this.detach()
    void this.ctx?.close()
    this.ctx = null
  }
}
